/**
 * Phase 101fv (Feb 2026) — Dialog "Purger factures de test".
 *
 * Alain (prod) : « Bouton admin pour nettoyer les 35 factures BROUILLON
 * "Bistro Test *" qui polluent /factures et faussent le compteur annuel. »
 *
 * Flow :
 * 1. Ouverture → GET /admin/purge-test-invoices/preview (auto)
 * 2. Affiche count + somme TTC + échantillon → l'admin voit exactement
 *    ce qui va disparaître avant d'appuyer
 * 3. Bouton "Supprimer les N factures" → POST /admin/purge-test-invoices
 *    avec confirm_count (protection anti-race si un autre admin a modifié
 *    entre-temps → 409 → redemande un preview frais)
 * 4. Sur succès : toast + fermeture + reload de la page /factures
 */
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Loader2, Trash2 } from "lucide-react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../ui/dialog";

export function PurgeTestInvoicesDialog({ open, onClose, onPurged }) {
  const [loading, setLoading] = useState(false);
  const [purging, setPurging] = useState(false);
  const [preview, setPreview] = useState(null);

  const loadPreview = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/purge-test-invoices/preview");
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur aperçu");
      onClose?.();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (open) loadPreview(); /* eslint-disable-next-line */ }, [open]);

  const submit = async () => {
    if (!preview || preview.count === 0) return;
    setPurging(true);
    try {
      const { data } = await api.post("/admin/purge-test-invoices", {
        pattern: preview.pattern,
        confirm_count: preview.count,
      });
      toast.success(`✅ ${data.deleted_count} facture(s) de test supprimée(s)`);
      onPurged?.();
    } catch (e) {
      if (e?.response?.status === 409) {
        toast.warning("Le nombre a changé — nouvel aperçu");
        loadPreview();
      } else {
        toast.error(e?.response?.data?.detail || "Erreur purge");
      }
    } finally {
      setPurging(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="purge-test-invoices-dialog">
        <DialogHeader>
          <DialogTitle>Purger factures de test</DialogTitle>
          <DialogDescription>
            Supprime définitivement les factures <strong>BROUILLON</strong> dont
            le nom du débiteur commence par « <code className="text-[#8C6B2F]">Bistro Test</code> ».
            Les factures envoyées, payées ou annulées ne sont <strong>jamais</strong> touchées.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="text-center py-8 text-[#575A57]">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
            Analyse en cours…
          </div>
        )}

        {!loading && preview && (
          <div className="space-y-4 py-2">
            {preview.count === 0 ? (
              <div className="text-center py-6 text-sm text-[#88A066]" data-testid="purge-preview-empty">
                ✅ Aucune facture de test à purger — la base est propre.
              </div>
            ) : (
              <>
                <div className="flex items-center gap-3 p-3 rounded border border-[#E8D3A8] bg-[#FBF4E9]">
                  <AlertTriangle className="w-5 h-5 text-[#8C6B2F] flex-shrink-0" />
                  <div className="text-sm text-[#8C6B2F]">
                    <strong data-testid="purge-preview-count">{preview.count}</strong> facture{preview.count > 1 ? "s" : ""} correspondront à la purge.
                    {" "}
                    Somme des TTC : <span className="font-mono">{preview.sum_ttc.toFixed(2)} CHF</span>
                    {preview.sum_ttc > 0 && (
                      <span className="block mt-1 text-[#E54D2E]">
                        ⚠️ TTC ≠ 0 — vérifie l'aperçu avant de supprimer, ces factures pourraient avoir de la valeur.
                      </span>
                    )}
                  </div>
                </div>

                <div>
                  <div className="text-xs uppercase tracking-wide text-[#575A57] mb-2">Aperçu (10 premières)</div>
                  <div className="max-h-64 overflow-y-auto border border-[#E5E5E0] rounded">
                    {preview.sample.map((inv) => (
                      <div
                        key={inv.id}
                        className="flex items-center justify-between px-3 py-2 border-b border-[#E5E5E0] last:border-b-0 text-sm"
                        data-testid={`purge-sample-${inv.reference}`}
                      >
                        <div className="flex items-center gap-3">
                          <span className="font-mono text-xs text-[#575A57]">{inv.reference}</span>
                          <span className="text-[#1A1C1A]">{inv.debtor_name || "—"}</span>
                        </div>
                        <Badge variant="outline" className="text-xs font-mono">
                          {(inv.total_ttc || 0).toFixed(2)} CHF
                        </Badge>
                      </div>
                    ))}
                    {preview.count > preview.sample.length && (
                      <div className="px-3 py-2 text-xs text-center text-[#A3A6A3] bg-[#FAFAF7]">
                        + {preview.count - preview.sample.length} autre{preview.count - preview.sample.length > 1 ? "s" : ""}
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={purging}
            data-testid="purge-cancel-btn"
          >
            Fermer
          </Button>
          {preview && preview.count > 0 && (
            <Button
              onClick={submit}
              disabled={purging || loading}
              className="bg-[#E54D2E] hover:bg-[#B03B1F] text-white"
              data-testid="purge-confirm-btn"
            >
              {purging ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              {purging ? "Suppression…" : `Supprimer les ${preview.count} facture${preview.count > 1 ? "s" : ""}`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
